import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import LateralPanel from '../../../../muiComponents/LateralPanel';
import {
    ITaxComplementaryDataOption,
    ITaxState,
} from '../../../../types/taxes/taxesTypes';
import useTranslate from '../../../../customHooks/useTranslate';
import Title from '../../yourProperties/Title';
import Icon from '../../../../muiComponents/Icon';
import TextInput from '../../../../muiComponents/TextInput/TextInput';
import LoadedOptionsTypeahead from '../../../../muiComponents/LoadedOptionsTypeahead';
import DateInput from '../../../../muiComponents/DateInput';
import Tooltip from '../../../../muiComponents/Tooltip';
import SwitchInput from '../../../../muiComponents/switchInput/SwitchInput';
import {
    readOnlyEntities,
    savableEntities,
} from '../../../../utils/commonVariables/entityKeys';
import { useODataMultipleQueries } from '../../../../customHooks/useODataMultipleQueries';
import {
    useTaxesComplementaryData,
    useTaxesList,
} from '../../../contextsProperty/ContextTaxes';
import IconRadioGroup from '../../../../muiComponents/iconRadioGroup/IconRadioGroup';
import {
    formatSupplierTaxCalculationRuleTypes,
    PANEL_VALIDATION_INITIAL_STATE,
    panelColumnsSpacing,
    panelRowsSpacing,
    panelTaxDoubleFieldColumnGrid,
    panelTaxFullSizeFieldColumnGrid,
    panelTaxNameFieldColumnGrid,
    panelTaxSingleFieldColumnGrid,
    supplementCalculationTypeOrder,
    supplierTaxCalculationRuleTypeFrequencyIcons,
    supplierTaxCalculationRuleTypeFrequencyOrder,
    supplierTaxCalculationRuleTypeSubjectIcons,
    supplierTaxCalculationTypesOrder,
    supplierTaxTypesOrder,
    TAX_DATE_TAG_COLOR,
    TAX_INITIAL_STATE,
    UnknownOptionIcons,
} from '../../../../utils/taxes/taxesListUtils';
import Grid from '../../../../muiComponents/Grid';
import GridChild from '../../../../muiComponents/GridChild';
import Button from '../../../../muiComponents/Button';
import { categorizePeriod, isDateless } from '../../../../utils/formatDates';
import Tag from '../../../../muiComponents/Tag';
import { useSearchParams } from '../../../../customHooks/useSearchParams';
import {
    getTaxData,
    saveTaxToDDBB,
} from '../../../../services/taxes/taxesServices';
import { adaptTaxFromDDBB } from '../../../../services/taxes/taxesAdapters';
import { useNotification } from '../../../../contexts/NotificationsContext';
import LoadingTaxPanel from '../LoadingTaxPanel';
import { validateForm } from '../../../../utils/validateForms';
import TypeAdditionalInfo from './TypeAdditionalInfo';
import SupplementAdditionalInfo from './SupplementAdditionalInfo';
import { FeedbackMessages } from '../../../../utils/commonVariables/messages';
import { convertToOvariant } from '../../../../utils/convertToOvariant';
import endpoints from '../../../../utils/commonVariables/endpoints';
import api from '../../../../services/api';
import {
    doubleMD_ColumnGrid,
    singleLG_DoubleMD_ColumnGrid,
} from '../../../../utils/commonVariables/grid';
import { SupplementAppliesTo } from '../../../../types/supplement/supplementsTypes';
import { appliesToOptions } from '../../../../utils/supplements/supplementUtils';
import useUserProfile from '../../../../customHooks/useUserProfile';
import ReadOnlyUserAlert from '../../ReadOnlyUserAlert';

interface TaxPanelProps {
    open: boolean;
    onClose: () => void;
}

const TaxPanel = (props: TaxPanelProps) => {
    const { open, onClose } = props;
    const translate = useTranslate();
    const { readOnlyUser } = useUserProfile();

    const { setErrorMessage, setSuccessMessage } = useNotification();

    const {
        supplierHotels,
        supplierTaxTypes,
        supplierTaxCalculationTypes,
        supplierTaxCalculationRuleTypes,
        supplementCalculationTypes,
        currencies,
    } = useTaxesComplementaryData();
    const { handleSearch } = useTaxesList();

    const { getSearchParam, removeSearchParam } = useSearchParams({
        base64: true,
    });

    const [detailTax, setDetailTax] = useState<ITaxState>(TAX_INITIAL_STATE);
    const [ruleTypeAppliesBy, setRuleTypeAppliesBy] = useState<string>('');
    const [ruleTypeFrequency, setRuleTypeFrequency] = useState<string>('');
    const [appliesTo, setAppliesTo] = useState<SupplementAppliesTo>(
        SupplementAppliesTo.ALL
    );
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState(
        PANEL_VALIDATION_INITIAL_STATE
    );

    const IdSupplierHotel = detailTax?.IdSupplierHotel || null;

    const idUrl = getSearchParam('id');
    const getTaxDetail = async (id: string) => {
        try {
            setIsLoading(true);
            const { data } = await getTaxData(id);
            if (data.Result && data.StatusCode === 200) {
                setDetailTax(adaptTaxFromDDBB(data.Result));
            } else {
                removeSearchParam('id');
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        } catch (error) {
            removeSearchParam('id');
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (idUrl) {
            if (idUrl !== 'new') {
                getTaxDetail(idUrl);
            }
        }
    }, [idUrl]);

    useEffect(() => {
        if (ruleTypeAppliesBy && ruleTypeFrequency) {
            const matchingRuleType = supplierTaxCalculationRuleTypes.data?.find(
                ruleType =>
                    ruleType.Key?.includes(ruleTypeAppliesBy) &&
                    ruleType.Key?.includes(ruleTypeFrequency)
            );
            if (matchingRuleType) {
                handleDetailTaxChange({
                    IdSupplierTaxCalculationRuleType: parseInt(
                        matchingRuleType.Id
                    ),
                    EffectivePerson:
                        ruleTypeAppliesBy === 'PERSON'
                            ? detailTax?.EffectivePerson || 0
                            : 0,
                });
            }
        }
    }, [ruleTypeAppliesBy, ruleTypeFrequency]);

    useEffect(() => {
        if (detailTax?.IdSupplierTaxCalculationRuleType) {
            const matchingRuleType = supplierTaxCalculationRuleTypes.data?.find(
                ruleType =>
                    parseInt(ruleType.Id) ===
                    detailTax.IdSupplierTaxCalculationRuleType
            );

            if (matchingRuleType?.Key) {
                const match = matchingRuleType.Key?.match(
                    /^PER([A-Z]+)PER([A-Z]+)$/
                );
                if (match) {
                    const [, subject, frequency] = match;
                    setRuleTypeAppliesBy(subject.toUpperCase());
                    setRuleTypeFrequency(frequency.toUpperCase());
                }
            }
        }
    }, [
        detailTax.IdSupplierTaxCalculationRuleType,
        supplierTaxCalculationRuleTypes,
    ]);

    const tagText = categorizePeriod(detailTax?.FromDate, detailTax?.ToDate);

    const supplierHotelDependentQueries = [
        {
            key: 'supplierHotelRoomTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRoomType,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelMealPlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelMealPlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelRatePlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRatePlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
    ];

    const {
        supplierHotelRoomTypes,
        supplierHotelMealPlans,
        supplierHotelRatePlans,
    } = useODataMultipleQueries({
        queries: supplierHotelDependentQueries,
        dependencies: [IdSupplierHotel],
        condition: !!IdSupplierHotel,
    });

    const supplierTaxCalculationRuleTypesOptions = useMemo(() => {
        return formatSupplierTaxCalculationRuleTypes(
            supplierTaxCalculationRuleTypes.data || []
        );
    }, [supplierTaxCalculationRuleTypes]);

    const selectedSupplierHotel = supplierHotels.data?.find(
        hotel => parseInt(hotel.Id) === detailTax?.IdSupplierHotel
    );

    const FIXED_ID_TAX_CALCULATION_TYPE = 4;
    const isFixedTaxId = (id: number) => id === FIXED_ID_TAX_CALCULATION_TYPE;

    const isPerPersonRule = ruleTypeAppliesBy === 'PERSON';

    const ID_SUPPLEMENT_CALCULATION_TYPE_FOR_EXCLUSIVE_TAX = 1;
    const TAX_TYPE_EXCLUSIVE = 'EXCLUSIVE';
    const TAX_TYPE_OTHER = 'OTHER';
    const isExclusiveTaxTypeKey =
        supplierTaxTypes.data?.find(
            type => parseInt(type.Id) === detailTax?.IdSupplierTaxType
        )?.Key === TAX_TYPE_EXCLUSIVE;

    const handleDetailTaxChange = (updatedFields: Partial<ITaxState>) => {
        setDetailTax(prev => {
            return prev ? { ...prev, ...updatedFields } : prev;
        });
    };

    const isNew = !detailTax?.Id || parseInt(detailTax.Id) <= 0;
    const datelessFrom = isDateless(detailTax?.FromDate);
    const datelessTo = isDateless(detailTax?.ToDate);

    const isDatelessTax = datelessFrom && datelessTo;

    const validateFields = {
        Name: {
            value: detailTax?.Name,
            validators: ['requiredString'],
        },
        ToDate: {
            value: [detailTax?.FromDate, detailTax?.ToDate],
            inputKey: 'ToDate',
            validators: ['fromToDate'],
        },
        IdSupplierHotel: {
            value: detailTax?.IdSupplierHotel,
            validators: ['requiredIdEntity'],
        },
        IdSupplierTaxType: {
            value: detailTax?.IdSupplierTaxType,
            validators: ['requiredIdEntity'],
        },
        IdSupplierTaxCalculationType: {
            value: detailTax?.IdSupplierTaxCalculationType,
            validators: ['requiredIdEntity'],
        },
        AppliesBy: {
            value: ruleTypeAppliesBy,
            validators: ['requiredString'],
        },
        Frequency: {
            value: ruleTypeFrequency,
            validators: ['requiredString'],
        },
        ...(isExclusiveTaxTypeKey
            ? {
                  IdSupplementCalculationType: {
                      value: detailTax?.IdSupplementCalculationType,
                      validators: ['requiredIdEntity'],
                  },
              }
            : {}),
        ...(isFixedTaxId(detailTax?.IdSupplierTaxCalculationType || 0)
            ? {
                  IdCurrency: {
                      value: detailTax?.IdCurrency,
                      validators: ['requiredIdEntity'],
                  },
              }
            : {}),
    };

    const handleValidateFields = () => {
        setFormErrors(prev => ({
            ...PANEL_VALIDATION_INITIAL_STATE,
            isValidated: prev.isValidated,
        }));
        if (!formErrors.isValidated) {
            setFormErrors(prev => ({
                ...prev,
                isValidated: true,
            }));
        }
        const errors = validateForm(validateFields);
        if (errors.length) {
            for (const error of errors) {
                setFormErrors(prev => ({
                    ...prev,
                    [error.inputKey]: error.errorMessage,
                }));
            }
        }
        return errors.length === 0;
    };

    const saveDDBBTax = async () => {
        const validated = handleValidateFields();
        if (!validated) return;
        if (detailTax) {
            try {
                setIsLoading(true);
                const { data } = await saveTaxToDDBB(detailTax);
                if (data) {
                    setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
                    handleSearch();
                    onClose();
                    handleResetPopUp();
                } else {
                    setErrorMessage(FeedbackMessages.COMMON_ERROR);
                }
            } catch (error) {
                console.log(error);
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleDeleteTax = async () => {
        if (isNew) {
            onClose();
            handleResetPopUp();
            return;
        }
        if (detailTax) {
            try {
                setIsLoading(true);
                const payload = convertToOvariant(savableEntities.SupplierTax, {
                    Ids: [detailTax.Id],
                });
                const endpoint = endpoints.commonDeleteItems;
                const res = await api.post(endpoint, payload);
                if (res.data && res.status === 200) {
                    setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_DELETE);
                    handleSearch();
                    onClose();
                    handleResetPopUp();
                } else {
                    setErrorMessage(FeedbackMessages.COMMON_ERROR);
                }
            } catch (error) {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleResetPopUp = () => {
        setDetailTax(TAX_INITIAL_STATE);
        setFormErrors(PANEL_VALIDATION_INITIAL_STATE);
        setRuleTypeAppliesBy('');
        setRuleTypeFrequency('');
        setAppliesTo(SupplementAppliesTo.ALL);
    };

    useEffect(() => {
        if (detailTax?.OnlyChildren) {
            setAppliesTo(SupplementAppliesTo.ONLY_CHILDREN);
        }
        if (detailTax?.OnlyAdults) {
            setAppliesTo(SupplementAppliesTo.ONLY_ADULTS);
        }
        if (!detailTax?.OnlyAdults && !detailTax?.OnlyChildren) {
            setAppliesTo(SupplementAppliesTo.ALL);
        }
    }, [detailTax.OnlyAdults, detailTax.OnlyChildren]);

    return (
        <LateralPanel open={open} className='--xl-width padding-bottom-32'>
            <header className='flex flex-between flex-align-center '>
                <Title
                    text={detailTax?.Name || translate('new_tax')}
                    className='text-secondary-color'
                    translated={!detailTax?.Name}
                />
                <div className='flex flex-align-center gap-20'>
                    {!isDatelessTax && (
                        <Tag
                            text={tagText}
                            color={TAX_DATE_TAG_COLOR[tagText]}
                        />
                    )}
                    <Tooltip
                        title={translate(
                            detailTax?.Enabled ? 'enabled' : 'disabled'
                        )}
                    >
                        <div>
                            <SwitchInput
                                disabled={isLoading || readOnlyUser}
                                checked={detailTax?.Enabled || false}
                                labelText={''}
                                onChangeMethod={(
                                    _e: ChangeEvent<HTMLInputElement>,
                                    v: boolean
                                ) => {
                                    handleDetailTaxChange({ Enabled: v });
                                }}
                            />
                        </div>
                    </Tooltip>
                    <Icon
                        iconName='Close'
                        className='cursor-pointer'
                        onClick={() =>
                            !isLoading && (onClose(), handleResetPopUp())
                        }
                    />
                </div>
            </header>
            <div className='padding-block-20'>
                <ReadOnlyUserAlert text='read_only_taxes' />
            </div>

            {isLoading ? (
                <main className='flex-column gap-20 height100perc padding-right-20 overflow-y-auto overflow-x-hidden padding-top-16'>
                    <LoadingTaxPanel />
                </main>
            ) : (
                <main className='flex-column gap-20 height100perc padding-right-20 overflow-y-auto overflow-x-hidden padding-top-16'>
                    <Grid
                        columnSpacing={panelColumnsSpacing}
                        rowSpacing={panelRowsSpacing}
                        style={{ width: 'auto' }}
                    >
                        <GridChild gridProps={panelTaxNameFieldColumnGrid}>
                            <TextInput
                                required
                                labelText='name'
                                value={detailTax?.Name}
                                handleChange={(
                                    e: ChangeEvent<HTMLTextAreaElement>
                                ) => {
                                    handleDetailTaxChange({
                                        Name: e.target.value,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                error={!!formErrors.Name}
                                errorMessage={formErrors.Name}
                                disabled={isLoading || readOnlyUser}
                            />
                        </GridChild>
                        <GridChild gridProps={panelTaxSingleFieldColumnGrid}>
                            <LoadedOptionsTypeahead
                                required
                                disabled={
                                    supplierHotels.isLoading ||
                                    !isNew ||
                                    readOnlyUser
                                }
                                labelText='hotel'
                                options={supplierHotels.data || []}
                                idValue={detailTax?.IdSupplierHotel}
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: ITaxComplementaryDataOption | null
                                ) => {
                                    handleDetailTaxChange({
                                        IdSupplierHotel: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                error={!!formErrors.IdSupplierHotel}
                                errorMessage={formErrors.IdSupplierHotel}
                            />
                        </GridChild>
                        <GridChild gridProps={panelTaxSingleFieldColumnGrid}>
                            <LoadedOptionsTypeahead
                                labelText='room'
                                options={supplierHotelRoomTypes.data || []}
                                idValue={detailTax?.IdSupplierHotelRoomType}
                                disabled={
                                    !detailTax?.IdSupplierHotel ||
                                    supplierHotelRoomTypes.isLoading ||
                                    readOnlyUser
                                }
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: ITaxComplementaryDataOption | null
                                ) => {
                                    handleDetailTaxChange({
                                        IdSupplierHotelRoomType: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                            />
                        </GridChild>
                        <GridChild gridProps={panelTaxSingleFieldColumnGrid}>
                            <LoadedOptionsTypeahead
                                labelText='meal_plan'
                                disabled={
                                    !detailTax?.IdSupplierHotel ||
                                    supplierHotelMealPlans.isLoading ||
                                    readOnlyUser
                                }
                                options={supplierHotelMealPlans.data || []}
                                idValue={detailTax?.IdSupplierHotelMealPlan}
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: ITaxComplementaryDataOption | null
                                ) => {
                                    handleDetailTaxChange({
                                        IdSupplierHotelMealPlan: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                            />
                        </GridChild>
                        <GridChild gridProps={panelTaxSingleFieldColumnGrid}>
                            <LoadedOptionsTypeahead
                                labelText='rate_plan'
                                disabled={
                                    !detailTax?.IdSupplierHotel ||
                                    supplierHotelRatePlans.isLoading ||
                                    readOnlyUser
                                }
                                options={supplierHotelRatePlans.data || []}
                                idValue={detailTax?.IdSupplierHotelRatePlan}
                                handleChange={(
                                    _event: React.SyntheticEvent,
                                    value: ITaxComplementaryDataOption | null
                                ) => {
                                    handleDetailTaxChange({
                                        IdSupplierHotelRatePlan: value
                                            ? parseInt(value.Id)
                                            : 0,
                                    });
                                }}
                            />
                        </GridChild>
                        <GridChild gridProps={panelTaxSingleFieldColumnGrid}>
                            <DateInput
                                labelText='from'
                                value={detailTax?.FromDate}
                                onChange={(date: Date | null) => {
                                    handleDetailTaxChange({
                                        FromDate: date,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                maxDate={detailTax?.ToDate || undefined}
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                        <GridChild gridProps={panelTaxSingleFieldColumnGrid}>
                            <DateInput
                                labelText='to'
                                value={detailTax?.ToDate}
                                onChange={(date: Date | null) => {
                                    handleDetailTaxChange({
                                        ToDate: date,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                minDate={detailTax?.FromDate || undefined}
                                error={!!formErrors.ToDate}
                                errorMessage={formErrors.ToDate}
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                    </Grid>
                    <Grid
                        columnSpacing={panelColumnsSpacing}
                        rowSpacing={panelRowsSpacing}
                        style={{
                            borderBlock: '1px solid var(--border)',
                            marginBlock: '10px',
                            width: 'auto',
                        }}
                    >
                        <GridChild gridProps={panelTaxDoubleFieldColumnGrid}>
                            <IconRadioGroup
                                disabled={readOnlyUser}
                                label={translate('type')}
                                info={<TypeAdditionalInfo />}
                                value={detailTax?.IdSupplierTaxType || 0}
                                onChange={value => {
                                    const isExclusive =
                                        supplierTaxTypes.data?.find(
                                            type =>
                                                parseInt(type.Id) ===
                                                parseInt(value.toString())
                                        )?.Key === TAX_TYPE_EXCLUSIVE;
                                    handleDetailTaxChange({
                                        IdSupplierTaxType: parseInt(
                                            value.toString()
                                        ),
                                        IdSupplementCalculationType:
                                            !isExclusive
                                                ? ID_SUPPLEMENT_CALCULATION_TYPE_FOR_EXCLUSIVE_TAX
                                                : detailTax?.IdSupplementCalculationType ||
                                                  0,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                options={(supplierTaxTypes.data || [])
                                    .sort((a, b) => {
                                        const order = supplierTaxTypesOrder;
                                        if (a?.Key && b?.Key)
                                            return (
                                                order.indexOf(a?.Key) -
                                                order.indexOf(b?.Key)
                                            );

                                        return 0;
                                    })
                                    .map(
                                        (
                                            type: ITaxComplementaryDataOption
                                        ) => ({
                                            value: parseInt(type.Id),
                                            label: type.Name,
                                            icon: type.Icon,
                                            disabled:
                                                type.Key === TAX_TYPE_OTHER,
                                        })
                                    )}
                                error={!!formErrors.IdSupplierTaxType}
                                errorMessage={formErrors.IdSupplierTaxType}
                            />
                        </GridChild>
                        <GridChild gridProps={panelTaxDoubleFieldColumnGrid}>
                            <IconRadioGroup
                                disabled={readOnlyUser}
                                label={translate('calculation_type')}
                                value={
                                    detailTax?.IdSupplierTaxCalculationType || 0
                                }
                                onChange={value => {
                                    handleDetailTaxChange({
                                        IdSupplierTaxCalculationType: parseInt(
                                            value.toString()
                                        ),
                                        IdCurrency: isFixedTaxId(
                                            parseInt(value.toString())
                                        )
                                            ? selectedSupplierHotel?.IdCurrency
                                            : detailTax?.IdCurrency || 0,
                                    });
                                }}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                options={(
                                    supplierTaxCalculationTypes.data || []
                                )
                                    .sort((a, b) => {
                                        const order =
                                            supplierTaxCalculationTypesOrder;
                                        if (a?.Key && b?.Key)
                                            return (
                                                order.indexOf(a?.Key) -
                                                order.indexOf(b?.Key)
                                            );

                                        return 0;
                                    })
                                    .map(
                                        (
                                            type: ITaxComplementaryDataOption
                                        ) => ({
                                            value: parseInt(type.Id),
                                            label: type.Name,
                                            icon: type.Icon,
                                        })
                                    )}
                                error={
                                    !!formErrors.IdSupplierTaxCalculationType
                                }
                                errorMessage={
                                    formErrors.IdSupplierTaxCalculationType
                                }
                            />
                        </GridChild>
                        <GridChild gridProps={panelTaxDoubleFieldColumnGrid}>
                            <IconRadioGroup
                                disabled={readOnlyUser}
                                label={translate('applies_by')}
                                value={ruleTypeAppliesBy}
                                onChange={value => {
                                    setRuleTypeAppliesBy(value.toString());
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                options={(
                                    supplierTaxCalculationRuleTypesOptions.subjects ||
                                    []
                                ).map(subj => ({
                                    value: subj,
                                    label: subj,
                                    icon:
                                        supplierTaxCalculationRuleTypeSubjectIcons[
                                            subj as keyof typeof supplierTaxCalculationRuleTypeSubjectIcons
                                        ] ||
                                        UnknownOptionIcons.SUPPLIER_TAX_CALCULATION_RULE_SUBJECT_TYPE,
                                }))}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                error={!!formErrors.AppliesBy}
                                errorMessage={formErrors.AppliesBy}
                            />
                        </GridChild>
                        <GridChild gridProps={panelTaxDoubleFieldColumnGrid}>
                            <IconRadioGroup
                                disabled={readOnlyUser}
                                label={translate('frequency')}
                                value={ruleTypeFrequency}
                                onChange={value => {
                                    setRuleTypeFrequency(value.toString());
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                options={(
                                    supplierTaxCalculationRuleTypesOptions.frequencies ||
                                    []
                                )
                                    .sort((a, b) => {
                                        const order =
                                            supplierTaxCalculationRuleTypeFrequencyOrder;
                                        if (a && b)
                                            return (
                                                order.indexOf(a) -
                                                order.indexOf(b)
                                            );

                                        return 0;
                                    })
                                    .map(subj => ({
                                        value: subj,
                                        label: subj,
                                        icon:
                                            supplierTaxCalculationRuleTypeFrequencyIcons[
                                                subj as keyof typeof supplierTaxCalculationRuleTypeFrequencyIcons
                                            ] ||
                                            UnknownOptionIcons.SUPPLIER_TAX_CALCULATION_RULE_FREQUENCY_TYPE,
                                    }))}
                                onBlur={() => {
                                    if (formErrors.isValidated) {
                                        handleValidateFields();
                                    }
                                }}
                                error={!!formErrors.Frequency}
                                errorMessage={formErrors.Frequency}
                            />
                        </GridChild>
                        {isExclusiveTaxTypeKey && (
                            <GridChild
                                gridProps={panelTaxDoubleFieldColumnGrid}
                            >
                                <IconRadioGroup
                                    disabled={readOnlyUser}
                                    label={translate('supplement')}
                                    info={<SupplementAdditionalInfo />}
                                    value={
                                        detailTax?.IdSupplementCalculationType ||
                                        0
                                    }
                                    onChange={value => {
                                        handleDetailTaxChange({
                                            IdSupplementCalculationType:
                                                parseInt(value.toString()),
                                        });
                                    }}
                                    options={(
                                        supplementCalculationTypes.data || []
                                    )
                                        .sort((a, b) => {
                                            const order =
                                                supplementCalculationTypeOrder;
                                            if (a?.Key && b?.Key)
                                                return (
                                                    order.indexOf(a?.Key) -
                                                    order.indexOf(b?.Key)
                                                );

                                            return 0;
                                        })
                                        .map(
                                            (
                                                type: ITaxComplementaryDataOption
                                            ) => ({
                                                value: parseInt(type.Id),
                                                label: type.Name,
                                                icon: type.Icon,
                                            })
                                        )}
                                    onBlur={() => {
                                        if (formErrors.isValidated) {
                                            handleValidateFields();
                                        }
                                    }}
                                    error={
                                        !!formErrors.IdSupplementCalculationType
                                    }
                                    errorMessage={
                                        formErrors.IdSupplementCalculationType
                                    }
                                />
                            </GridChild>
                        )}
                        {isPerPersonRule ? (
                            <GridChild gridProps={doubleMD_ColumnGrid}>
                                <IconRadioGroup
                                    disabled={readOnlyUser}
                                    label={translate('age_segment')}
                                    value={appliesTo}
                                    onChange={value => {
                                        const updatedFields: Partial<ITaxState> =
                                            {
                                                OnlyAdults: false,
                                                OnlyChildren: false,
                                            };
                                        if (
                                            value ===
                                            SupplementAppliesTo.ONLY_ADULTS
                                        ) {
                                            updatedFields.OnlyAdults = true;
                                        }
                                        if (
                                            value ===
                                            SupplementAppliesTo.ONLY_CHILDREN
                                        ) {
                                            updatedFields.OnlyChildren = true;
                                        }
                                        handleDetailTaxChange(updatedFields);
                                    }}
                                    options={appliesToOptions}
                                />
                            </GridChild>
                        ) : (
                            ''
                        )}
                    </Grid>
                    <Grid
                        columnSpacing={panelColumnsSpacing}
                        rowSpacing={panelRowsSpacing}
                        style={{ width: 'auto' }}
                    >
                        <GridChild gridProps={panelTaxSingleFieldColumnGrid}>
                            <TextInput
                                labelText='amount'
                                value={detailTax?.Amount}
                                type='number'
                                handleChange={(
                                    e: ChangeEvent<HTMLTextAreaElement>
                                ) =>
                                    handleDetailTaxChange({
                                        Amount: parseFloat(e.target.value),
                                    })
                                }
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                        {detailTax?.IdSupplierTaxCalculationType &&
                        isFixedTaxId(
                            detailTax?.IdSupplierTaxCalculationType
                        ) ? (
                            <GridChild
                                gridProps={panelTaxSingleFieldColumnGrid}
                            >
                                <LoadedOptionsTypeahead
                                    labelText='currency'
                                    options={currencies.data || []}
                                    idValue={detailTax?.IdCurrency}
                                    handleChange={(
                                        _event: React.SyntheticEvent,
                                        value: ITaxComplementaryDataOption | null
                                    ) => {
                                        handleDetailTaxChange({
                                            IdCurrency: value
                                                ? parseInt(value.Id)
                                                : 0,
                                        });
                                    }}
                                    onBlur={() => {
                                        if (formErrors.isValidated) {
                                            handleValidateFields();
                                        }
                                    }}
                                    error={!!formErrors.IdCurrency}
                                    errorMessage={formErrors.IdCurrency}
                                    disabled={readOnlyUser}
                                />
                            </GridChild>
                        ) : (
                            ''
                        )}
                        <GridChild gridProps={panelTaxSingleFieldColumnGrid}>
                            <TextInput
                                labelText='priority'
                                value={detailTax?.Priority.toString()}
                                type='number'
                                handleChange={(
                                    e: ChangeEvent<HTMLTextAreaElement>
                                ) =>
                                    handleDetailTaxChange({
                                        Priority: parseInt(e.target.value),
                                    })
                                }
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                        {isPerPersonRule && (
                            <GridChild
                                gridProps={panelTaxSingleFieldColumnGrid}
                            >
                                <TextInput
                                    labelText='effective_person'
                                    value={detailTax?.EffectivePerson}
                                    type='number'
                                    handleChange={(
                                        e: ChangeEvent<HTMLTextAreaElement>
                                    ) =>
                                        handleDetailTaxChange({
                                            EffectivePerson: parseInt(
                                                e.target.value
                                            ),
                                        })
                                    }
                                    helperText={
                                        <span className='flex gap-5 flex-align-start'>
                                            <Icon
                                                iconName='InfoOutlined'
                                                style={{
                                                    marginTop: '2px',
                                                    fontSize: '12px',
                                                }}
                                            />
                                            {translate(
                                                'effective_person_description'
                                            )}
                                        </span>
                                    }
                                    disabled={readOnlyUser}
                                />
                            </GridChild>
                        )}
                        {isPerPersonRule ? (
                            <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                                <TextInput
                                    labelText='from_age'
                                    value={detailTax?.FromAge}
                                    type='number'
                                    handleChange={(
                                        e: ChangeEvent<HTMLTextAreaElement>
                                    ) =>
                                        handleDetailTaxChange({
                                            FromAge: parseInt(e.target.value),
                                        })
                                    }
                                    disabled={readOnlyUser}
                                />
                            </GridChild>
                        ) : (
                            ''
                        )}
                        {isPerPersonRule ? (
                            <GridChild gridProps={singleLG_DoubleMD_ColumnGrid}>
                                <TextInput
                                    labelText='to_age'
                                    value={detailTax?.ToAge}
                                    type='number'
                                    handleChange={(
                                        e: ChangeEvent<HTMLTextAreaElement>
                                    ) =>
                                        handleDetailTaxChange({
                                            ToAge: parseInt(e.target.value),
                                        })
                                    }
                                    disabled={readOnlyUser}
                                />
                            </GridChild>
                        ) : (
                            ''
                        )}
                        <GridChild gridProps={panelTaxFullSizeFieldColumnGrid}>
                            <TextInput
                                labelText='description2'
                                value={detailTax?.Description}
                                rows={5}
                                multiline
                                handleChange={(
                                    e: ChangeEvent<HTMLTextAreaElement>
                                ) =>
                                    handleDetailTaxChange({
                                        Description: e.target.value,
                                    })
                                }
                                disabled={readOnlyUser}
                            />
                        </GridChild>
                    </Grid>
                </main>
            )}

            <footer className='flex flex-between margin-block-16'>
                <Button
                    variant='contained'
                    className={'delete-button'}
                    disabled={isLoading || readOnlyUser}
                    onClick={() => {
                        handleDeleteTax();
                    }}
                >
                    {translate('delete')}
                </Button>
                <div className='flex gap-20'>
                    <Button
                        variant='outlined'
                        disabled={isLoading}
                        onClick={() => {
                            onClose();
                            handleResetPopUp();
                        }}
                    >
                        {translate('cancel')}
                    </Button>
                    <Button
                        variant='contained'
                        disabled={isLoading || readOnlyUser}
                        onClick={saveDDBBTax}
                    >
                        {translate('save')}
                    </Button>
                </div>
            </footer>
        </LateralPanel>
    );
};

export default TaxPanel;
